<template>
  <v-card elevation="0" class="pa-8 login-card" max-width="480">

    <!-- ── Step 1: Credentials ──────────────────────────────────────── -->
    <template v-if="!twoFactorStep">
      <div class="text-center mb-8">
       <!--  <h2 class="text-h4 font-weight-bold mb-2">Welcome Back</h2>-->
        <p class="text-body-1 text-grey-darken-1">Sign in to continue to your account</p>
      </div> 

      <v-form @submit.prevent="handleLogin" ref="loginForm" v-model="formValid">
        <v-text-field
          v-model="form.email"
          label="Email Address"
          type="email"
          variant="outlined"
          prepend-inner-icon="mdi-email-outline"
          :rules="emailRules"
          :error-messages="errors.email"
          color="primary"
          class="mb-4"
          autocomplete="email"
        />

        <v-text-field
          v-model="form.password"
          label="Password"
          :type="showPassword ? 'text' : 'password'"
          variant="outlined"
          prepend-inner-icon="mdi-lock-outline"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword"
          :rules="passwordRules"
          :error-messages="errors.password"
          color="primary"
          class="mb-2"
          autocomplete="current-password"
        />

        <div class="d-flex justify-space-between align-center mb-6">
          <v-checkbox
            v-model="form.rememberMe"
            label="Remember me"
            color="primary"
            hide-details
            density="compact"
          />
          <v-btn
            variant="text"
            color="primary"
            size="small"
            class="text-none"
            @click="$router.push('/auth/forgot-password')"
          >
            Forgot password?
          </v-btn>
        </div>

        <v-btn
          type="submit"
          color="primary"
          size="x-large"
          block
          :loading="loading"
          :disabled="!formValid"
          class="mb-4 text-none"
          rounded="lg"
        >
          <v-icon left class="mr-2">mdi-login</v-icon>
          Sign In
        </v-btn>
      </v-form>
    </template>

    <!-- ── Step 2: OTP Verification ─────────────────────────────────── -->
    <template v-else>
      <div class="text-center mb-8">
        <v-icon size="64" color="primary" class="mb-4">mdi-shield-key</v-icon>
        <h2 class="text-h5 font-weight-bold mb-2">Two-Factor Verification</h2>
        <p class="text-body-2 text-grey-darken-1">{{ twoFactorMessage }}</p>
      </div>

      <v-form @submit.prevent="handleVerify2fa" ref="otpForm" v-model="otpFormValid">
        <!-- Individual digit boxes -->
        <div class="otp-wrapper mb-2">
          <v-text-field
            v-for="(_, i) in otpDigits"
            :key="i"
            v-model="otpDigits[i]"
            :ref="el => { if (el) otpRefs[i] = el }"
            maxlength="1"
            variant="outlined"
            density="comfortable"
            hide-details
            class="otp-digit"
            inputmode="numeric"
            @input="onOtpInput(i)"
            @keydown.backspace="onOtpBackspace(i)"
            @paste.prevent="onOtpPaste($event)"
          />
        </div>
        <p class="text-caption text-center text-medium-emphasis mb-6">
          Enter the 6-digit code from your email
        </p>

        <v-btn
          type="submit"
          color="primary"
          size="x-large"
          block
          :loading="loading"
          :disabled="otpCode.length < 6"
          class="mb-4 text-none"
          rounded="lg"
        >
          <v-icon left class="mr-2">mdi-check-circle</v-icon>
          Verify & Sign In
        </v-btn>

        <div class="text-center">
          <v-btn
            variant="text"
            color="primary"
            size="small"
            class="text-none"
            :disabled="resendCooldown > 0"
            @click="resendCode"
          >
            <v-icon start size="small">mdi-refresh</v-icon>
            {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code' }}
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            class="text-none ml-2"
            @click="cancelTwoFactor"
          >
            ← Back to login
          </v-btn>
        </div>
      </v-form>
    </template>

    <!-- Footer -->
    <div class="text-center mt-6">
      <p class="text-caption text-grey">© 2026 Board Matters. All rights reserved.</p>
    </div>
  </v-card>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useToast } from 'vue-toastification'

export default {
  name: 'Login',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    const toast = useToast()

    // ── Step 1 state ────────────────────────────────────────────────
    const loginForm = ref(null)
    const formValid = ref(false)
    const showPassword = ref(false)
    const loading = ref(false)

    const form = reactive({ email: '', password: '', rememberMe: false })
    const errors = reactive({ email: [], password: [] })

    const emailRules = [
      v => !!v || 'Email is required',
      v => /.+@.+\..+/.test(v) || 'Email must be valid'
    ]
    const passwordRules = [
      v => !!v || 'Password is required',
      v => v.length >= 6 || 'Password must be at least 6 characters'
    ]

    // ── Step 2 (2FA) state ──────────────────────────────────────────
    const twoFactorStep = ref(false)
    const twoFactorMessage = ref('')
    const preAuthToken = ref('')
    const otpForm = ref(null)
    const otpFormValid = ref(false)
    const otpDigits = reactive(['', '', '', '', '', ''])
    const otpRefs = ref([])
    const resendCooldown = ref(0)
    let cooldownTimer = null

    const otpCode = computed(() => otpDigits.join(''))

    // ── OTP input helpers ────────────────────────────────────────────
    const onOtpInput = (index) => {
      const val = otpDigits[index]
      if (val && index < 5) {
        otpRefs.value[index + 1]?.$el?.querySelector('input')?.focus()
      }
    }

    const onOtpBackspace = (index) => {
      if (!otpDigits[index] && index > 0) {
        otpDigits[index - 1] = ''
        otpRefs.value[index - 1]?.$el?.querySelector('input')?.focus()
      }
    }

    const onOtpPaste = (e) => {
      const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6)
      text.split('').forEach((ch, i) => { otpDigits[i] = ch })
      const last = Math.min(text.length, 5)
      otpRefs.value[last]?.$el?.querySelector('input')?.focus()
    }

    // ── Cooldown timer ───────────────────────────────────────────────
    const startCooldown = () => {
      resendCooldown.value = 60
      clearInterval(cooldownTimer)
      cooldownTimer = setInterval(() => {
        resendCooldown.value--
        if (resendCooldown.value <= 0) clearInterval(cooldownTimer)
      }, 1000)
    }

    // ── Actions ──────────────────────────────────────────────────────
    const clearErrors = () => { errors.email = []; errors.password = [] }

    const quickLogin = (email, password) => {
      form.email = email.trim()
      form.password = password
      handleLogin()
    }

    const handleLogin = async () => {
      if (!formValid.value) return
      clearErrors()
      loading.value = true

      try {
        const result = await authStore.login({ email: form.email, password: form.password })

        if (result.requires_2fa) {
          twoFactorStep.value = true
          preAuthToken.value = result.pre_auth_token
          twoFactorMessage.value = result.message
          startCooldown()
          return
        }

        if (result.success) {
          toast.success(`Welcome back, ${result.user.first_name}!`)
          router.push(route.query.redirect || '/')
        } else {
          toast.error(result.message)
        }
      } catch (error) {
        toast.error('An unexpected error occurred. Please try again.')
      } finally {
        loading.value = false
      }
    }

    const handleVerify2fa = async () => {
      if (otpCode.value.length < 6) return
      loading.value = true

      try {
        const result = await authStore.verify2fa({
          pre_auth_token: preAuthToken.value,
          code: otpCode.value
        })

        if (result.success) {
          toast.success(`Welcome back, ${result.user.first_name}!`)
          router.push(route.query.redirect || '/')
        } else {
          toast.error(result.message)
          // Clear digits on failure
          otpDigits.forEach((_, i) => { otpDigits[i] = '' })
          otpRefs.value[0]?.$el?.querySelector('input')?.focus()
        }
      } catch (error) {
        toast.error('Verification failed. Please try again.')
      } finally {
        loading.value = false
      }
    }

    const resendCode = async () => {
      if (resendCooldown.value > 0) return
      loading.value = true
      try {
        const result = await authStore.login({ email: form.email, password: form.password })
        if (result.requires_2fa) {
          preAuthToken.value = result.pre_auth_token
          twoFactorMessage.value = result.message
          otpDigits.forEach((_, i) => { otpDigits[i] = '' })
          startCooldown()
          toast.info('A new code has been sent to your email')
        }
      } finally {
        loading.value = false
      }
    }

    const cancelTwoFactor = () => {
      twoFactorStep.value = false
      preAuthToken.value = ''
      otpDigits.forEach((_, i) => { otpDigits[i] = '' })
      clearInterval(cooldownTimer)
    }

    return {
      loginForm, formValid, showPassword, loading, form, errors,
      emailRules, passwordRules, handleLogin, quickLogin,
      twoFactorStep, twoFactorMessage, otpForm, otpFormValid,
      otpDigits, otpRefs, otpCode, resendCooldown,
      onOtpInput, onOtpBackspace, onOtpPaste,
      handleVerify2fa, resendCode, cancelTwoFactor
    }
  }
}
</script>

<style scoped>
.login-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08) !important;
  animation: fadeIn 0.6s ease-out;
}

/* OTP digit boxes */
.otp-wrapper {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.otp-digit {
  width: 52px;
  flex: 0 0 52px;
}

.otp-digit :deep(input) {
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0;
  padding: 10px 0;
}

.cursor-pointer {
  cursor: pointer;
  transition: transform 0.2s ease;
}
.cursor-pointer:hover { transform: translateY(-2px); }
.gap-2 { gap: 0.5rem; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
